import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { PlayIcon, EnvelopeSimpleIcon } from "@phosphor-icons/react";
import { fetchGrokReport7d, fetchGrokDigestPreview, runGrokJobs } from "../../lib/api";

const fmt = (s) => (s ? new Date(s).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—");

function Row({ cells, testId }) {
  return (
    <div data-testid={testId} className="grid grid-cols-12 gap-2 py-1.5 text-xs border-b border-zinc-100 last:border-0">
      {cells.map((c, i) => <div key={i} className={c.cls}>{c.v}</div>)}
    </div>
  );
}

export function Report7dPanel({ reloadKey }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { try { setR(await fetchGrokReport7d()); } catch { /* hidden */ } }, []);
  useEffect(() => { load(); }, [load, reloadKey]);

  const run = async () => {
    setBusy(true);
    try {
      const x = await runGrokJobs();
      toast.success(`Follow-ups: ${x.followups.mode} · ${x.followups.logged} logged, ${x.followups.queued} queued`);
      await load();
    } catch { toast.error("Run failed"); } finally { setBusy(false); }
  };
  const preview = async () => {
    try {
      const d = await fetchGrokDigestPreview();
      const w = window.open("", "_blank");
      if (w) { w.document.write(d.html); w.document.title = d.subject; } else toast.error("Allow pop-ups to preview");
    } catch { toast.error("Preview failed"); }
  };
  if (!r) return null;
  const f = r.followups;

  return (
    <div className="border border-zinc-200 bg-white p-5 mb-6" data-testid="grok-report7d-panel">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div className="flex-1">
          <div className="overline mb-1">Last 7 days · what would have been sent</div>
          <div className="font-heading font-black text-lg">Round 2 dry-run</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="grok-digest-preview-btn" onClick={preview} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-zinc-300 bg-white hover:bg-zinc-50 inline-flex items-center gap-1.5">
            <EnvelopeSimpleIcon size={14} weight="bold" /> Preview digest
          </button>
          <button data-testid="grok-run-jobs-btn" disabled={busy} onClick={run} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center gap-1.5">
            <PlayIcon size={14} weight="bold" /> Run jobs now
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        {[["Follow-ups that would send", f.would_send, "r7-would-send"],
          [`Older intros excluded (before ${r.cutoff.start_date})`, r.cutoff.excluded_older_intros, "r7-excluded-cutoff"], ["Blocked (replied/opt-out/you)", f.blocked, "r7-blocked"],
          ["Moved by quiet hours", f.moved_by_quiet_hours, "r7-quiet"], ["Digests simulated (1 per day)", r.digests.would_send, "r7-digests"]].map(([l, v, t]) => (
          <div key={t} className="border border-zinc-200 p-3" data-testid={t}>
            <div className="overline mb-1">{l}</div><div className="font-heading font-black text-2xl font-mono-num">{v}</div>
          </div>
        ))}
      </div>
      {f.items.length > 0 && (
        <div className="mb-4" data-testid="r7-followup-list">
          <div className="text-xs font-semibold mb-1">Follow-ups that would have been sent</div>
          {f.items.slice(0, 30).map((x, i) => (
            <Row key={i} testId={`r7-fu-${i}`} cells={[{ v: x.contact, cls: "col-span-4 truncate" }, { v: `#${x.followup}`, cls: "col-span-1" },
              { v: fmt(x.would_send_at), cls: "col-span-4 font-mono-num" }, { v: x.moved_by_quiet_hours ? "after quiet hours" : x.tz, cls: "col-span-3 text-zinc-400 truncate" }]} />
          ))}
        </div>
      )}
      {r.dry_run_logged.items.length > 0 && (
        <div className="mb-4" data-testid="r7-logged-list">
          <div className="text-xs font-semibold mb-1">Logged by the bot (not sent)</div>
          {r.dry_run_logged.items.slice(0, 20).map((x, i) => (
            <div key={i} className="text-xs border-l-2 border-[#FFCC00] pl-2 py-1 mb-1"><span className="text-zinc-400">{x.kind || "reply"}{x.n ? ` #${x.n}` : ""} · </span>{x.text}</div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid="r7-digest-days">
        {r.digests.days.map((d) => (
          <span key={d.date} className="px-2 py-1 text-[11px] border border-zinc-200 font-mono-num">{d.date} · {d.new_leads} leads · {d.new_high} high</span>
        ))}
      </div>
      {r.notes.map((n, i) => <div key={i} className="text-xs text-zinc-600 bg-[#FFCC00]/15 border border-[#FFCC00]/50 p-2.5 mt-2" data-testid={`r7-note-${i}`}>{n}</div>)}
    </div>
  );
}
